import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../../../features/token/tokenSaver';
import {
    getPendingPersonalContent,
    approvePersonalContent,
    rejectPersonalContent,
} from '../../../services/personalContentReview';
import './AdminDashboardContentReview.css';

function formatDate(value) {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return String(value);
    }
}

function hostLabel(row) {
    const name = [row.HostFname, row.HostLname].filter(Boolean).join(' ').trim();
    return name || row.HostEmail || row.HostPhone || `User #${row.UserId}`;
}

function screenLabel(row) {
    return row.ScreenName || row.ScreenAddress || row.ScreenCity || `Screen #${row.ScreenId}`;
}

function displaySlotNumber(slotId) {
    const n = parseInt(slotId, 10);
    if (n >= 7 && n <= 12) return n - 6;
    return slotId;
}

function isVideoUrl(url) {
    if (!url) return false;
    return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

function renderPreview(url, alt) {
    if (!url) return <div className="contentReviewPreviewEmpty">No preview</div>;
    if (isVideoUrl(url)) {
        return (
            <video
                src={url}
                className="contentReviewPreviewMedia"
                controls
                muted
                playsInline
                loop
            />
        );
    }
    return <img src={url} alt={alt} className="contentReviewPreviewMedia" />;
}

export default function AdminDashboardContentReview() {
    const token = useSelector(getToken);
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionKey, setActionKey] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        getPendingPersonalContent(token)
            .then((res) => {
                setRows(res.data.submissions || []);
            })
            .catch(() => {
                setError('Could not load pending personal content.');
                setRows([]);
            })
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    async function handleApprove(row) {
        const reviewNotes = window.prompt('Optional note to send the host (leave blank for none):') || '';
        const key = `${row.ScreenId}-${row.SlotId}-approve`;
        setActionKey(key);
        try {
            await approvePersonalContent(
                row.ScreenId,
                row.SlotId,
                { userId: row.UserId, reviewNotes: reviewNotes || undefined },
                token,
            );
            load();
        } catch (err) {
            window.alert(err?.response?.data?.error || 'Failed to approve content.');
        } finally {
            setActionKey('');
        }
    }

    async function handleReject(row) {
        const reviewNotes = window.prompt('Optional note to send the host (leave blank for none):') || '';
        const key = `${row.ScreenId}-${row.SlotId}-reject`;
        setActionKey(key);
        try {
            await rejectPersonalContent(
                row.ScreenId,
                row.SlotId,
                { userId: row.UserId, reviewNotes: reviewNotes || undefined },
                token,
            );
            load();
        } catch (err) {
            window.alert(err?.response?.data?.error || 'Failed to reject content.');
        } finally {
            setActionKey('');
        }
    }

    return (
        <div className="adminDashboardContainer">
            <div className="contentReviewPage">
                <div className="contentReviewHeader">
                    <h1 className="contentReviewTitle">Personal content review</h1>
                    <span className="contentReviewSubtitle">
                        Host submissions waiting for approval before they appear on screens.
                    </span>
                </div>

                {error && <div className="contentReviewError">{error}</div>}

                {loading ? (
                    <p className="contentReviewMuted">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="contentReviewMuted">No pending personal content submissions.</p>
                ) : (
                    <div className="contentReviewGrid">
                        {rows.map((row) => {
                            const key = `${row.ScreenId}-${row.SlotId}-${row.UserId}`;
                            const busy = actionKey.startsWith(`${row.ScreenId}-${row.SlotId}`);
                            return (
                                <article className="contentReviewCard" key={key}>
                                    <div className="contentReviewCardHeader">
                                        <div>
                                            <h2 className="contentReviewCardTitle">{screenLabel(row)}</h2>
                                            <p className="contentReviewCardMeta">
                                                Host: {hostLabel(row)} · Slot {displaySlotNumber(row.SlotId)}
                                            </p>
                                            <p className="contentReviewCardMeta">
                                                Submitted {formatDate(row.PendingContentSubmittedAt)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="contentReviewLinkBtn"
                                            onClick={() => navigate(`/adminDashboard/screens/details/${row.ScreenId}`)}
                                        >
                                            Open screen
                                        </button>
                                    </div>

                                    <div className="contentReviewPreviewRow">
                                        <div className="contentReviewPreviewBlock">
                                            <span className="contentReviewPreviewLabel">Pending</span>
                                            {renderPreview(row.PendingContentLink, 'Pending content')}
                                        </div>
                                        {row.LiveContentLink && (
                                            <div className="contentReviewPreviewBlock">
                                                <span className="contentReviewPreviewLabel">Current live</span>
                                                {renderPreview(row.LiveContentLink, 'Live content')}
                                            </div>
                                        )}
                                    </div>

                                    <div className="contentReviewActions">
                                        <button
                                            type="button"
                                            className="contentReviewApproveBtn"
                                            onClick={() => handleApprove(row)}
                                            disabled={busy}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            type="button"
                                            className="contentReviewRejectBtn"
                                            onClick={() => handleReject(row)}
                                            disabled={busy}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
